import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useCallback } from "react";
import throttle from "lodash-es/throttle";
import { getAuth, sendEmailVerification, createUserWithEmailAndPassword } from "firebase/auth";
import { AppDispatch } from "app/store";
import firebase from "app/Auth";
import { actions as configActions } from "app/configSlice";
import { Select } from "common/elements/Select/Select";
import { btnDelay, useScale } from "common/utils";
import parentI18n from "features/shared/i18n";
import { updateLastSignInTime } from "features/account/api";
import { useSignUp } from "./hook";
import { BackBtn } from "./BackBtn";
import { MobileInput } from "./AuthStyles";
import { useAuthLocale } from "./useAuthLocale";

export const EmailRegisterScreen = () => {
  useSignUp();
  const { currentLocale, changeLocale, localeOptions, localeLabels } = useAuthLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { width, height, scale } = useScale();
  const auth = getAuth(firebase);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localeOpen, setLocaleOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  const registerRef = useRef({ email, password, confirmPassword });
  registerRef.current = { email, password, confirmPassword };

  const handleToggleLocale = () => {
    btnDelay(() => {
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    changeLocale(locale);
  };

  const handleRegister = useCallback(
    throttle(
      async () => {
        const { email, password, confirmPassword } = registerRef.current;
        setIsLoading(true);

        if (!password || !confirmPassword) {
          setError(parentI18n.t("MSG_SIGN_PASSWORD_REQUIRED"));
          setIsLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError(parentI18n.t("MSG_SIGN_PASSWORD_MISMATCH"));
          setIsLoading(false);
          return;
        }

        setError("");

        try {
          const actionCodeSettings = {
            url: import.meta.env.VITE_PUBLIC_URL,
            handleCodeInApp: false,
          };
          createUserWithEmailAndPassword(auth, email, password)
            .then(() => {
              updateLastSignInTime();
              dispatch(configActions.setJustAuthenticated(true));
              sendEmailVerification(auth.currentUser, actionCodeSettings);
              navigate("/");
            })
            .catch((error) => {
              console.error(error);
              if (error.code === "auth/email-already-in-use") {
                setError(parentI18n.t("MSG_SIGN_UP_EMAIL_ALREADY_EXISTS_FAILED_MESSAGE"));
              }
            })
            .finally(() => {
              setIsLoading(false);
            });
        } catch (err) {
          console.error(err);
          setError(parentI18n.t("MSG_SIGN_UP_FAILED"));
          setIsLoading(false);
        }
      },
      3000,
      { leading: true, trailing: false },
    ),
    [auth, navigate],
  );

  const handleOpenLink = (url: string) => {
    if ("ReactNativeWebView" in window) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ command: "openLink", data: { url } }));
    } else {
      window.open(url, "_blank");
    }
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* ヘッダー */}
      <div style={{ height: 90 * scale }} className="flex-col-el flex-center">
        <header
          style={{ transform: `scale(${scale})`, width: width / scale }}
          className="flex-row-el h-[90px] items-center px-6 py-4 border-b border-outline bg-white"
        >
          <BackBtn />
          <p className="ml-6 text-[27px] text-textcolor-black">
            {parentI18n.t("MSG_SIGN_UP_WITH_MAIL")}
          </p>
          {/* 言語選択ボタン - 右端に配置 */}
          <div className="ml-auto flex-row-el items-center gap-3">
            <span className="text-[20px] text-textcolor-black w-[140px] text-right">
              {parentI18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
            </span>
            <button
              ref={langBtnRef}
              onClick={handleToggleLocale}
              className="border-0 bg-green-100 flex-row-el items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all w-[150px]"
            >
              <span className="material-symbols-outlined text-white !text-[20px]">language</span>
              <span className="text-body text-white text-[16px] flex-1 text-center">
                {localeLabels[currentLocale] || localeLabels["ja_kanji"]}
              </span>
              <span className="material-symbols-outlined text-white !text-[16px]">expand_more</span>
            </button>
          </div>
          <Select
            anchorRef={langBtnRef}
            open={localeOpen}
            onClose={handleCloseLocale}
            value={currentLocale}
            onChange={handleChangeLocale}
            options={[...localeOptions]}
          />
        </header>
      </div>

      {/* メインコンテンツ */}
      <div
        style={{ height: height - (90 + 52) * scale }}
        className="flex-col-el items-center justify-start w-full bg-white/40"
      >
        <main
          style={{
            width: width / scale,
            height: (height - (90 + 52) * scale) / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div className="bg-white rounded-[16px] p-8 max-w-md w-full">
            <div className="flex-col-el flex-center w-full gap-4">
              <MobileInput
                type="email"
                value={email}
                onChange={(e) => {
                  setError("");
                  setEmail(e.target.value);
                }}
                placeholder={parentI18n.t("MSG_SIGN_USER_MAIL")}
                label={parentI18n.t("MSG_SIGN_USER_MAIL")}
                disabled={isLoading}
              />

              <MobileInput
                type="password"
                value={password}
                onChange={(e) => {
                  setError("");
                  setPassword(e.target.value);
                }}
                placeholder={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
                label={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
                disabled={isLoading}
              />

              <MobileInput
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setError("");
                  setConfirmPassword(e.target.value);
                }}
                placeholder={parentI18n.t("MSG_SIGN_USER_PASSWORD_CONFIRM")}
                label={parentI18n.t("MSG_SIGN_USER_PASSWORD_CONFIRM")}
                disabled={isLoading}
              />

              {error && <p className="text-[14px] text-danger text-center">{error}</p>}

              <button
                className="w-[375px] py-3.5 bg-green-100 text-white rounded-[12px] text-[18px] border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50"
                onClick={handleRegister}
                disabled={isLoading}
              >
                {parentI18n.t("MSG_SIGN_UP_BTN")}
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* フッター */}
      <div style={{ height: 52 * scale }} className="flex-col-el flex-center w-full bg-white/40">
        <footer
          style={{ transform: `scale(${scale})` }}
          className="flex-row-el flex-center w-[1280px] h-[52px] gap-4 px-6 py-4 text-sm text-gray2-100"
        >
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/product/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_GO_TO_SITE")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/privacy-policy/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_TERMS_OF_SERVICE")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/privacy-policy/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_PRIVACY_POLICY")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/contact/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_INQUIRY")}
          </span>
        </footer>
      </div>
    </div>
  );
};
